/**
 * The scoreboard on screen (67.14): the table from the match record (core/scoreboard.ts), shown on the pause overlay's
 * SCOREBOARD button during a game (Esc opens the overlay as today) and on the end-of-game card with the winner, how,
 * and the match's shape. Plain text with the fiction layer's words on `data-flavor`. The host (an action game) sets
 * the source while it runs and clears it when it ends; the view never reads the match itself.
 */
import type { ScoreTable } from '../core/scoreboard';
import { applyFlavorDom } from './flavor';
import { TEAMS } from '../constants';

const TEAM_COLORS = TEAMS.map(t => t.primary);
const STATUS_WORDS: Record<string, string> = { alive: 'up', seated: 'seated', held: 'you', down: 'down' };

let source: (() => ScoreTable) | null = null;

/** The running game's table (null when no game is running). */
export function setScoreboardSource(fn: (() => ScoreTable) | null): void {
  source = fn;
  const btn = document.getElementById('btn-ingame-scoreboard');
  if (btn) btn.style.display = fn ? '' : 'none';
  if (!fn) { const el = document.getElementById('ingame-scoreboard'); if (el) el.style.display = 'none'; }
}

/** The table as HTML: team rows (tickets, points, standing, kills), then a row per unit; the viewer's row marked. */
export function scoreTableHtml(t: ScoreTable): string {
  const col = (team: number) => TEAM_COLORS[team % TEAM_COLORS.length];
  const th = (s: string, flavor?: string) => `<th style="text-align:right; padding:2px 6px; font-weight:500; color:#9aa0b4;"${flavor ? ` data-flavor="${flavor}"` : ''}>${s}</th>`;
  const teams = `<div style="display:flex; gap:12px; flex-wrap:wrap; margin-bottom:8px;">` + t.teams.map(tm =>
    `<div style="border-left:3px solid ${col(tm.team)}; padding:2px 8px;"><b style="color:${col(tm.team)}">${tm.name}</b> <span style="color:#c8ccd8;">${tm.alive}/${tm.total} standing · ${tm.kills} kills${tm.tickets !== null ? ` · <span data-flavor="${tm.tickets} contracts">${tm.tickets} tickets</span>` : ''}${tm.points ? ` · ${tm.points} point${tm.points === 1 ? '' : 's'}` : ''}</span></div>`).join('') + `<div style="margin-left:auto; color:#9aa0b4;">${t.clock}</div></div>`;
  const rows = t.rows.map(r => `<tr style="${r.you ? 'background:rgba(255,255,255,0.08);' : ''}${r.status === 'down' ? ' opacity:0.55;' : ''}">`
    + `<td style="padding:2px 6px; border-left:3px solid ${col(r.team)};">${r.name}${r.you ? ' ◂' : ''}</td>`
    + `<td style="padding:2px 6px; color:#9aa0b4;">${STATUS_WORDS[r.status]}</td>`
    + `<td style="padding:2px 6px; color:#9aa0b4;">${r.doing}</td>`
    + [r.score.kills, r.score.deaths, r.score.captures, r.score.shots, r.score.hits].map(n => `<td style="text-align:right; padding:2px 6px; font-variant-numeric:tabular-nums;">${n}</td>`).join('')
    + `</tr>`).join('')
    // 67.53: the human's totals over every unit they held this match ("in case you switch units")
    + (t.you ? `<tr style="background:rgba(255,255,255,0.12); font-weight:600;"><td style="padding:2px 6px; border-left:3px solid #ffffff;">You</td><td style="padding:2px 6px; color:#9aa0b4;" colspan="2">every unit you held</td>`
      + [t.you.kills, t.you.deaths, t.you.captures, t.you.shots, t.you.hits].map(n => `<td style="text-align:right; padding:2px 6px; font-variant-numeric:tabular-nums;">${n}</td>`).join('') + `</tr>` : '');
  return teams + `<table style="width:100%; border-collapse:collapse; font-size:12px;"><thead><tr><th style="text-align:left; padding:2px 6px; font-weight:500; color:#9aa0b4;">unit</th><th style="text-align:left; padding:2px 6px; font-weight:500; color:#9aa0b4;">status</th><th style="text-align:left; padding:2px 6px; font-weight:500; color:#9aa0b4;">doing</th>${th('kills')}${th('deaths')}${th('captures')}${th('shots')}${th('hits')}</tr></thead><tbody>${rows}</tbody></table>`;
}

/** The pause overlay's SCOREBOARD button: show or hide the table in place (filled fresh each time). */
export function toggleIngameScoreboard(): void {
  const el = document.getElementById('ingame-scoreboard');
  if (!el || !source) return;
  const open = el.style.display === 'none' || !el.style.display;
  el.style.display = open ? 'block' : 'none';
  if (open) { el.innerHTML = scoreTableHtml(source()); applyFlavorDom(el); }
}

/** The end-of-game card: the winner and how, the match's shape in a line, the table; hidden by `hideEndCard`. */
export function showEndCard(o: { headline: string; tone: 'won' | 'lost' | 'neutral'; how: string; shape: string; table: ScoreTable; keys: string }): void {
  let el = document.getElementById('scoreboard-end') as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = 'scoreboard-end';
    el.style.cssText = 'position:fixed; left:50%; top:50%; transform:translate(-50%,-50%); width:min(760px, calc(100vw - 32px)); max-height:80vh; overflow-y:auto; background:rgba(12,12,24,0.96); border:1px solid rgba(255,255,255,0.15); border-radius:10px; padding:16px 18px; color:#e8eaf0; font-family:system-ui, sans-serif; z-index:50;';
    document.body.appendChild(el);
  }
  const color = o.tone === 'won' ? '#6fd08c' : o.tone === 'lost' ? '#e0864a' : '#c8ccd8';
  el.innerHTML = `<div style="font-size:11px; letter-spacing:0.1em; text-transform:uppercase; color:#9aa0b4;" data-flavor="CONTRACT CLOSED">MATCH OVER</div>`
    + `<div style="font-size:22px; color:${color}; margin:4px 0;">${o.headline}</div>`
    + `<div style="font-size:12px; color:#c8ccd8; margin-bottom:4px;">${o.how}</div>`
    + `<div style="font-size:12px; color:#9aa0b4; margin-bottom:12px;">${o.shape}</div>`
    + scoreTableHtml(o.table)
    + (o.keys ? `<div style="font-size:11px; color:#9aa0b4; margin-top:12px;">${o.keys}</div>` : '');
  el.style.display = 'block';
  applyFlavorDom(el);
}

export function hideEndCard(): void {
  const el = document.getElementById('scoreboard-end');
  if (el) el.style.display = 'none';
}
